// Mirrors the mgmt-api conditional-laser validation (Feature #3502,
// LaseredConditionTerms.Validate): a name, at least one ICD or CPT code, a
// deductible > 0 (the API rejects $0 with a 400) and four window dates with
// start <= end per window. Blocking errors return a message; "" means valid.
//
// Separately, a window outside the policy defaults is a NON-blocking warning
// (#7344): run-in lookbacks and carrier terms legitimately differ.

import type { ConditionWindows } from "@/utils/helpers/conditionWindowDefaults";

export interface LaseredConditionDraftInput extends ConditionWindows {
  lasered_condition_name: string;
  icd_codes: string[];
  cpt_codes: string[];
  /** Raw input (string from the currency field) or number. */
  amt_lasered_conditional_deductible: string | number;
}

const parseDay = (value: string): Date | null => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const d = new Date(`${value}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** Blocking validation for the four windows. */
export const validateConditionWindows = (w: ConditionWindows): string => {
  if (!w.incurred_start_date || !w.incurred_end_date || !w.paid_start_date || !w.paid_end_date) {
    return "All four window dates (incurred and paid) are required.";
  }
  const dates = [w.incurred_start_date, w.incurred_end_date, w.paid_start_date, w.paid_end_date].map(parseDay);
  if (dates.some((d) => d === null)) return "One or more window dates are invalid.";
  const [incStart, incEnd, paidStart, paidEnd] = dates as Date[];
  if (incStart > incEnd) return "Incurred start must be on or before incurred end.";
  if (paidStart > paidEnd) return "Paid start must be on or before paid end.";
  return "";
};

/** Blocking validation for one condition row. */
export const validateLaseredCondition = (c: LaseredConditionDraftInput): string => {
  if (!c.lasered_condition_name.trim()) return "A condition name is required.";
  const codes = [...c.icd_codes, ...c.cpt_codes].map((s) => s.trim()).filter(Boolean);
  if (codes.length === 0) return "Add at least one ICD or CPT code.";
  const amt =
    typeof c.amt_lasered_conditional_deductible === "number"
      ? c.amt_lasered_conditional_deductible
      : parseFloat(String(c.amt_lasered_conditional_deductible ?? ""));
  if (!Number.isFinite(amt) || amt <= 0) return "A conditional deductible greater than $0 is required.";
  return validateConditionWindows(c);
};

/** Case-insensitive duplicate names among rows, matching the API's unique index. */
export const findDuplicateConditionName = (names: string[]): string => {
  const seen = new Set<string>();
  for (const n of names) {
    const key = n.trim().toLowerCase();
    if (!key) continue;
    if (seen.has(key)) return n.trim();
    seen.add(key);
  }
  return "";
};

/**
 * Non-blocking: windows that start before or end after the policy defaults.
 * Returns "" when inside the defaults or when there are no defaults to compare.
 */
export const conditionWindowsOutsideDefaultsWarning = (w: ConditionWindows, defaults: ConditionWindows): string => {
  const outside = (start: string, end: string, defStart: string, defEnd: string) =>
    !!defStart && !!defEnd && !!start && !!end && (start < defStart || end > defEnd);
  const inc = outside(
    w.incurred_start_date,
    w.incurred_end_date,
    defaults.incurred_start_date,
    defaults.incurred_end_date
  );
  const paid = outside(w.paid_start_date, w.paid_end_date, defaults.paid_start_date, defaults.paid_end_date);
  if (!inc && !paid) return "";
  const which = inc && paid ? "Both windows extend" : inc ? "The incurred window extends" : "The paid window extends";
  return `${which} beyond the policy dates. That is allowed (run-in lookbacks, carrier terms); confirm it matches the laser notice.`;
};
